import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { AppState } from '@om-fe/core/core/types';
import {
    FilteredEventInfos,
    FilteredMatchesWithEvents,
    MatchListStoreModel,
} from '../match-list/utils/match-list-models';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { createBem, isLiveStatus, preventEvent } from '@om-fe/utils';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { makeGetServePossessionParticipantId } from '@om-fe/core/components/match-list/containers/live-match-list/live-match-list.selector';
import { IconSize } from '@om-fe/core/utils/config';
import EventAnchor from '@om-fe/core/components/event-anchor/event-anchor';
import MatchInfoDetails from './match-info-details';
import getSlice from '@om-fe/core/utils/get-slice';
import { ParticipantIcon } from '@om-fe/widgets';
import MatchInfoScore from './match-info-score';
import getConfig from '@om-fe/core/utils/config';
import FavoriteTeamsButton from '@om-fe/core/components/favorite-teams/favorite-teams-button';
import { EventStatus } from '@om-fe/models';

interface MatchInfoDefaultProps {
    readonly routeName: RouteNames;
}

interface MatchInfoPublicProps extends Partial<MatchInfoDefaultProps> {
    readonly matchId: string;
    readonly sliceKey: string;
    readonly isEventSlider?: boolean;
}

interface MatchInfoStoreProps {
    readonly isLive?: boolean;
    readonly isSmall?: boolean;
    readonly match?: FilteredMatchesWithEvents;
    readonly servePossessionParticipantId?: string;
}

type MatchInfoProps = MatchInfoPublicProps
    & MatchInfoDefaultProps
    & MatchInfoStoreProps
    & I18nContextInterface;

const bem = createBem({
    name: 'Details',
});

class MatchInfoComponent extends React.Component<MatchInfoProps> {
    static defaultProps: MatchInfoDefaultProps = {
        routeName: RouteNames.liveEvent,
    };

    getParticipantIcon(participantLogoUrl: string): React.ReactNode {
        const { match } = this.props;

        if (!match) {
            return;
        }

        return (
            <span className={bem('ParticipantIconWrapper')}>
                <ParticipantIcon
                    logoUrl={participantLogoUrl}
                    disciplineId={match.sportId}
                    size={IconSize.Medium2}
                />
            </span>
        );
    }

    render() {
        const {
            match,
            isSmall,
            matchId,
            sliceKey,
            servePossessionParticipantId,
            isLive,
        } = this.props;

        if (!match) {
            return null;
        }

        const isHyphenatedFullUrl: boolean = !!getConfig().enableHyphenatedUrl?.fullName;
        const locationName = isHyphenatedFullUrl ? match?.venueName : match?.shortVenueName;
        const disciplineName = isHyphenatedFullUrl ? match?.sportName : match?.shortSportName;
        const tournamentName = isHyphenatedFullUrl ? match?.parentName : match?.shortParentName;

        const {
            homeParticipantName, awayParticipantName, homeShortParticipantName, awayShortParticipantName,
            matchTimeId, remainingTimeId, currentEventPartId, homeParticipantId, awayParticipantId,
            homeParticipantLogoUrl, awayParticipantLogoUrl, sportId: disciplineId, name: eventName,
        } = match;

        const homeName: string = isSmall ? homeShortParticipantName : homeParticipantName;
        const awayName: string = isSmall ? awayShortParticipantName : awayParticipantName;
        const servePossessionHome = (homeParticipantId === servePossessionParticipantId);
        const servePossessionAway = (awayParticipantId === servePossessionParticipantId);

        return (
            <EventAnchor
                className={bem()}
                isLive={!!isLive}
                disciplineId={disciplineId}
                disciplineName={disciplineName}
                locationName={locationName}
                tournamentName={tournamentName}
                eventId={matchId}
                eventName={eventName}
            >
                <span className={bem('Group', { HasParticipantIcon: !!(homeParticipantLogoUrl || awayParticipantLogoUrl) })}>
                    <span className={bem('Participants')}>
                        <span className={bem('Participant', { Home: true, ServePossession: servePossessionHome })}>
                            {homeParticipantLogoUrl && this.getParticipantIcon(homeParticipantLogoUrl)}
                            <div className={bem('FavoriteButton')} onClick={preventEvent}>
                                <FavoriteTeamsButton participantId={homeParticipantId} />
                            </div>
                            <span className={bem('ParticipantName')}>
                                {homeName}
                            </span>
                        </span>
                        <span className={bem('Participant', { Away: true, ServePossession: servePossessionAway })}>
                            {awayParticipantLogoUrl && this.getParticipantIcon(awayParticipantLogoUrl)}
                            <div className={bem('FavoriteButton')} onClick={preventEvent}>
                                <FavoriteTeamsButton participantId={awayParticipantId} />
                            </div>
                            <span className={bem('ParticipantName')}>
                                {awayName}
                            </span>
                        </span>
                    </span>
                    <MatchInfoScore
                        matchId={matchId}
                        sliceKey={sliceKey}
                    />
                </span>
                <MatchInfoDetails
                    currentEventPartId={currentEventPartId}
                    matchId={matchId}
                    matchTimeId={matchTimeId}
                    remainingTimeId={remainingTimeId}
                    sliceKey={sliceKey}
                />
            </EventAnchor>
        );
    }
}

const MatchInfo: React.ComponentType<MatchInfoPublicProps> = compose<React.ComponentType<MatchInfoPublicProps>>(
    withTranslation<MatchInfoPublicProps>(),
    connect<MatchInfoStoreProps>(
        () => {
            const getServePossessionParticipantId = makeGetServePossessionParticipantId();

            return (state: AppState, props: MatchInfoPublicProps): MatchInfoStoreProps => {
                const { matchId } = props;
                const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, props.sliceKey);

                const breakpoints: BreakpointsState | null = state.breakpoints;

                if (!slice || !breakpoints) {
                    return {};
                }

                const match: FilteredMatchesWithEvents | undefined = slice.filteredMatchesWithEvents[matchId];
                const isSmall: boolean | undefined = breakpoints.small;
                const isLive: boolean = !!match && isLiveStatus(match.statusId as EventStatus);
                const eventInfo: Record<string, FilteredEventInfos> = slice.filteredEventInfos;

                return {
                    isLive,
                    isSmall,
                    match,
                    servePossessionParticipantId: getServePossessionParticipantId(eventInfo, matchId),
                };
            };
        },
    ),
)(MatchInfoComponent);
export default MatchInfo;
